import {
  EditSmIcon,
  LinkSmIcon,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@crono-one/design-system';
import UserBadge from 'components/UserBadge';
import PrivateLockIcon from 'crono-fe-common/icons/Icon-Private-Lock';
import { List } from 'crono-fe-common/types/list';
import { copyListShareLink, isPrivateList } from 'pages/lists/listSharing';
import { KeyboardEvent, MouseEvent, ReactNode } from 'react';
import { cn } from 'utils/style-utils';

interface ListRowActionProps {
  label: string;
  icon: ReactNode;
  onClick: () => void;
}

const ListRowAction = ({ label, icon, onClick }: ListRowActionProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <button
        type="button"
        aria-label={label}
        className="flex shrink-0 cursor-pointer border-0 bg-transparent p-0 text-gray-700 hover:text-gray-900"
        onClick={(e: MouseEvent) => {
          e.stopPropagation();
          onClick();
        }}
      >
        {icon}
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="z-999999999">
      {label}
    </TooltipContent>
  </Tooltip>
);

interface ListOptionRowProps {
  list: List;
  onSelect: () => void;
  disabled?: boolean;
  /** Rendered before the name, e.g. the checkbox of a multi-select picker. */
  leading?: ReactNode;
  onEdit?: (list: List) => void;
  /**
   * Hover actions: copy the share link (not for private lists) and, for lists
   * the user owns, edit.
   */
  withActions?: boolean;
}

const ListOptionRow = ({
  list,
  onSelect,
  disabled = false,
  leading,
  onEdit,
  withActions = false,
}: ListOptionRowProps) => {
  const isPrivate = isPrivateList(list);
  const canCopyLink = withActions && !isPrivate;
  const canEdit = withActions && list.owned && !!onEdit;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      className={cn(
        'group flex min-h-10 items-center gap-2 rounded-lg p-2',
        disabled
          ? 'cursor-default text-gray-600'
          : 'cursor-pointer text-gray-900 hover:bg-gray-100',
      )}
      onClick={() => {
        if (!disabled) onSelect();
      }}
      onKeyDown={(e: KeyboardEvent) => {
        if (e.target !== e.currentTarget || disabled) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {leading}

      {isPrivate && (
        <PrivateLockIcon
          color="currentColor"
          className="size-4 min-w-4 shrink-0"
        />
      )}

      <span className="min-w-0 flex-1 truncate text-xs font-medium leading-4">
        {list.name}
      </span>

      {(canCopyLink || canEdit) && (
        <div className="hidden shrink-0 items-center gap-2 group-hover:flex group-focus-within:flex">
          {canCopyLink && (
            <ListRowAction
              label="Copy share link"
              icon={<LinkSmIcon className="size-4" />}
              onClick={() => copyListShareLink(list.id)}
            />
          )}
          {canEdit && (
            <ListRowAction
              label="Edit list"
              icon={<EditSmIcon className="size-4" />}
              onClick={() => onEdit?.(list)}
            />
          )}
        </div>
      )}

      {!list.owned && (
        <UserBadge
          userName={list.owner}
          userId={list.userId}
          className="shrink-0"
        />
      )}
    </div>
  );
};

export default ListOptionRow;
